import * as pc from "playcanvas";

/**
 * GrenadeEffects — visual + sensory effects spawned at a grenade's
 * detonation point. Pure client-side; no networking, no damage.
 *
 * Each effect is self-contained: it builds its own entities, registers
 * its own `update` listener, and tears itself down when its lifetime
 * elapses. They don't fight each other, so multiple grenades can be
 * stacked without coordination.
 *
 * Damage / blind / smoke obstruction are TODO — they need a server
 * authority pass so peers experience the same world.
 */
export type GrenadeKind = "he" | "flash" | "smoke" | "molotov" | "incendiary";

interface ActiveEffect {
  age: number;
  duration: number;
  tick: (dt: number, age: number) => void;
  end: () => void;
}

function createSmokeTexture(app: pc.AppBase): pc.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  
  // Draw multiple overlapping radial gradients to create a "cloudy" puff
  // instead of a single perfect sphere.
  const drawPuff = (x: number, y: number, r: number, alpha: number) => {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
    gradient.addColorStop(0.4, `rgba(255, 255, 255, ${alpha * 0.7})`);
    gradient.addColorStop(0.8, `rgba(255, 255, 255, ${alpha * 0.1})`);
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  };

  // Center main puff
  drawPuff(size / 2, size / 2, size / 2, 0.9);
  // Off-center smaller puffs to break the spherical shape
  drawPuff(size * 0.35, size * 0.4, size * 0.35, 0.6);
  drawPuff(size * 0.65, size * 0.35, size * 0.3, 0.5);
  drawPuff(size * 0.5, size * 0.65, size * 0.35, 0.7);
  drawPuff(size * 0.25, size * 0.6, size * 0.25, 0.4);
  drawPuff(size * 0.75, size * 0.6, size * 0.25, 0.4);
  
  const texture = new pc.Texture(app.graphicsDevice, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
  });
  texture.setSource(canvas);
  return texture;
}

function createShardTexture(app: pc.AppBase): pc.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  // A small bright sliver of glass — light-blue/white triangle on a clear
  // background, modeled on the soft-puff style but sharp-edged.
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "rgba(0,0,0,0)";
  ctx.fillRect(0, 0, size, size);
  ctx.beginPath();
  ctx.moveTo(size * 0.5, size * 0.08);
  ctx.lineTo(size * 0.78, size * 0.92);
  ctx.lineTo(size * 0.22, size * 0.82);
  ctx.closePath();
  const grad = ctx.createLinearGradient(size * 0.5, 0, size * 0.5, size);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.5, "rgba(210,235,255,0.9)");
  grad.addColorStop(1, "rgba(160,205,245,0.5)");
  ctx.fillStyle = grad;
  ctx.fill();

  const texture = new pc.Texture(app.graphicsDevice, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
  });
  texture.setSource(canvas);
  return texture;
}

function createFireTexture(app: pc.AppBase): pc.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  
  // Draw overlapping radial gradients for a fiery, organic puff
  const drawPuff = (x: number, y: number, r: number, alpha: number) => {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
    gradient.addColorStop(0.2, `rgba(255, 220, 100, ${alpha * 0.9})`);
    gradient.addColorStop(0.5, `rgba(255, 100, 0, ${alpha * 0.6})`);
    gradient.addColorStop(0.8, `rgba(150, 20, 0, ${alpha * 0.2})`);
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  };

  // Center main puff
  drawPuff(size / 2, size / 2, size / 2, 1.0);
  // Off-center smaller puffs to break the spherical shape
  drawPuff(size * 0.35, size * 0.4, size * 0.35, 0.8);
  drawPuff(size * 0.65, size * 0.35, size * 0.3, 0.7);
  drawPuff(size * 0.5, size * 0.65, size * 0.35, 0.8);
  
  const texture = new pc.Texture(app.graphicsDevice, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
  });
  texture.setSource(canvas);
  return texture;
}

export class GrenadeEffects {
  private readonly app: pc.AppBase;
  private readonly camera: pc.Entity;
  private readonly active: ActiveEffect[] = [];
  /** Camera-shake pitch/yaw offsets, decayed each frame. */
  private shakePitch = 0;
  private shakeYaw = 0;
  /** Lazily-built soft puff texture, shared by every smoke cloud. */
  private smokeTex: pc.Texture | null = null;
  /** Persistent, server-driven smoke clouds — billboarded each frame. */
  private readonly smokeZones = new Set<SmokeCloud>();
  /** Lazily-built flame-tongue texture, shared by every fire zone. */
  private fireTex: pc.Texture | null = null;
  /** Persistent, server-driven fire pools — billboarded each frame. */
  private readonly fireZones = new Set<FireZone>();
  /** Lazily-built glass-shard texture, shared by every shatter burst. */
  private shardTex: pc.Texture | null = null;

  constructor(app: pc.AppBase, camera: pc.Entity) {
    this.app = app;
    this.camera = camera;
    this.app.on("update", this.onUpdate);
  }

  destroy(): void {
    this.app.off("update", this.onUpdate);
    for (const e of this.active) e.end();
    this.active.length = 0;
    for (const z of this.smokeZones) z.destroy();
    this.smokeZones.clear();
    for (const f of this.fireZones) f.destroy();
    this.fireZones.clear();
    this.applySmokeFog(0);
  }

  /**
   * Tear down every active effect immediately (smoke clouds, molotov
   * fire, debris) without removing the update listener — used on round
   * end so leftover smoke/fire from the previous round doesn't bleed
   * into the next one. Also clears the flashbang DOM overlay.
   */
  clearAll(): void {
    for (const e of this.active) e.end();
    this.active.length = 0;
    for (const z of this.smokeZones) z.destroy();
    this.smokeZones.clear();
    for (const f of this.fireZones) f.destroy();
    this.fireZones.clear();
    this.shakePitch = 0;
    this.shakeYaw = 0;
    this.applySmokeFog(0);
    const overlay = document.getElementById("flash-overlay");
    if (overlay) {
      overlay.style.transition = "none";
      overlay.style.opacity = "0";
    }
  }

  /** Spawn the right effect for the grenade type at the given world point.
   *  Flash blind is NOT decided here — it's server-authoritative; see
   *  `applyBlind`, driven by the `flash_blind` message. */
  spawn(kind: GrenadeKind, pos: pc.Vec3): void {
    switch (kind) {
      case "he":
        this.spawnHEExplosion(pos);
        break;
      case "flash":
        this.spawnFlashbang(pos);
        break;
      case "smoke":
        // Smoke is rendered by the persistent, server-authoritative zone
        // (PeerGrenadeRenderer → createSmokeZone); the one-shot detonation
        // adds no separate cloud here — that double-rendered with the zone.
        break;
      case "molotov":
      case "incendiary":
        // Fire is rendered by the persistent, server-authoritative zone
        // (PeerGrenadeRenderer → createFireZone); the one-shot detonation
        // adds no separate flames here — that double-rendered with the zone.
        break;
    }
  }

  // ── Frame loop ────────────────────────────────────────────────────────

  private readonly onUpdate = (dt: number) => {
    // Tick effects, prune expired.
    for (let i = this.active.length - 1; i >= 0; i--) {
      const e = this.active[i];
      e.age += dt;
      e.tick(dt, e.age);
      if (e.age >= e.duration) {
        e.end();
        this.active.splice(i, 1);
      }
    }
    // Persistent smoke clouds: billboard + roil every frame. Their lifetime,
    // position and opacity are server-driven (setAlpha); we only render.
    for (const z of this.smokeZones) {
      if (!z.alive) { this.smokeZones.delete(z); continue; }
      z.tick(dt);
    }
    // In-smoke screen fog — billboard puffs thin out when the camera stands
    // between them, so crossing a cloud used to feel tiny and see-through.
    // Grey the screen by penetration depth instead (CS:GO-style).
    let fog = 0;
    if (this.smokeZones.size > 0) {
      const camPos = this.camera.getPosition();
      for (const z of this.smokeZones) fog = Math.max(fog, z.fogAt(camPos));
    }
    this.applySmokeFog(fog);
    for (const f of this.fireZones) {
      if (!f.alive) { this.fireZones.delete(f); continue; }
      f.tick(dt);
    }
    // Decay camera shake each frame.
    if (this.shakePitch !== 0 || this.shakeYaw !== 0) {
      const decay = Math.max(0, 1 - dt * 9);
      this.shakePitch *= decay;
      this.shakeYaw *= decay;
      if (Math.abs(this.shakePitch) < 0.01) this.shakePitch = 0;
      if (Math.abs(this.shakeYaw) < 0.01) this.shakeYaw = 0;
    }
  };

  /** Last opacity written to the smoke overlay — skips redundant DOM writes. */
  private smokeFogLast = -1;

  /** Drive the in-smoke grey-out overlay (quantized to avoid DOM churn). */
  private applySmokeFog(fog: number): void {
    const q = Math.round(fog * 200) / 200;
    if (q === this.smokeFogLast) return;
    this.smokeFogLast = q;
    const overlay = document.getElementById("smoke-overlay");
    if (overlay) overlay.style.opacity = String(q);
  }

  /** Public hook for FirstPersonController to read + apply per-frame shake. */
  consumeShake(): { pitch: number; yaw: number } {
    return { pitch: this.shakePitch, yaw: this.shakeYaw };
  }

  /**
   * Sprint 5 — external systems (e.g. damage-taken shake) feed extra
   * impulses through here. The decay is shared with grenade shakes.
   */
  addShake(pitch: number, yaw: number): void {
    this.shakePitch += pitch;
    this.shakeYaw += yaw;
  }

  // ── HE explosion ──────────────────────────────────────────────────────

  private spawnHEExplosion(pos: pc.Vec3): void {
    const root = new pc.Entity("he-explosion");
    root.setPosition(pos.x, pos.y + 0.4, pos.z);

    // Light burst
    const light = new pc.Entity("he-light");
    light.addComponent("light", {
      type: "omni",
      color: new pc.Color(1.0, 0.65, 0.2),
      intensity: 25,
      range: 16,
      castShadows: false,
    });
    root.addChild(light);

    const fireTex = this.getFireTexture();
    const smokeTex = this.getSmokeTexture();

    // 1. Core Fireball Burst (particles)
    const fireball = new pc.Entity("he-fireball");
    fireball.addComponent("particlesystem", {
      numParticles: 40,
      lifetime: 0.6,
      rate: 0.005, // 0.2s emission
      colorMap: fireTex,
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.5,
      velocityGraph: new pc.CurveSet([[0, 12, 1, 0], [0, 12, 1, 0], [0, 12, 1, 0]]),
      velocityGraph2: new pc.CurveSet([[0, -12, 1, 0], [0, -12, 1, 0], [0, -12, 1, 0]]),
      scaleGraph: new pc.Curve([0, 1.5, 0.3, 4.0, 1, 5.0]),
      alphaGraph: new pc.Curve([0, 1, 0.4, 1, 1, 0]),
      colorGraph: new pc.CurveSet([
        [0, 1, 0.5, 1, 1, 0.6], 
        [0, 1, 0.5, 0.5, 1, 0.1], 
        [0, 1, 0.5, 0.1, 1, 0.0]  
      ]),
      blendType: pc.BLEND_ADDITIVE,
      loop: false,
      depthWrite: false,
      startAngle: 0,
      startAngle2: 360,
    });
    root.addChild(fireball);
    if (fireball.particlesystem) fireball.particlesystem.play();

    // 2. Thick Smoke Billow (particles)
    const smoke = new pc.Entity("he-smoke");
    smoke.addComponent("particlesystem", {
      numParticles: 50,
      lifetime: 2.5,
      rate: 0.01, // 0.5s emission
      colorMap: smokeTex,
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.8,
      velocityGraph: new pc.CurveSet([[0, 8, 1, 0.5], [0, 10, 1, 1], [0, 8, 1, 0.5]]),
      velocityGraph2: new pc.CurveSet([[0, -8, 1, -0.5], [0, 2, 1, 0.5], [0, -8, 1, -0.5]]),
      scaleGraph: new pc.Curve([0, 2.0, 0.3, 5.0, 1, 7.5]),
      alphaGraph: new pc.Curve([0, 0, 0.1, 0.9, 0.6, 0.9, 1, 0]),
      colorGraph: new pc.CurveSet([
        [0, 0.25, 1, 0.1],
        [0, 0.22, 1, 0.09],
        [0, 0.18, 1, 0.08]
      ]),
      blendType: pc.BLEND_NORMAL,
      loop: false,
      depthWrite: false,
      startAngle: 0,
      startAngle2: 360,
    });
    root.addChild(smoke);
    if (smoke.particlesystem) smoke.particlesystem.play();

    // 3. High-velocity Sparks/Debris (particles)
    const sparks = new pc.Entity("he-sparks");
    sparks.addComponent("particlesystem", {
      numParticles: 120,
      lifetime: 1.2,
      rate: 0.002, // 0.24s emission
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.2,
      velocityGraph: new pc.CurveSet([[0, 30, 1, 0], [0, 30, 1, -15], [0, 30, 1, 0]]), // Burst out, then fall
      velocityGraph2: new pc.CurveSet([[0, -30, 1, 0], [0, 5, 1, -15], [0, -30, 1, 0]]),
      scaleGraph: new pc.Curve([0, 0.15, 1, 0.02]),
      alphaGraph: new pc.Curve([0, 1, 0.6, 1, 1, 0]),
      colorGraph: new pc.CurveSet([
        [0, 1, 1, 1],
        [0, 0.9, 1, 0.3],
        [0, 0.5, 1, 0]
      ]),
      blendType: pc.BLEND_ADDITIVE,
      loop: false,
      depthWrite: false,
    });
    root.addChild(sparks);
    if (sparks.particlesystem) sparks.particlesystem.play();

    // 4. Ground Shockwave (expanding ring)
    const shockwave = new pc.Entity("he-shockwave");
    shockwave.addComponent("render", { type: "cylinder" });
    const shockMat = new pc.StandardMaterial();
    shockMat.diffuse = new pc.Color(0, 0, 0);
    shockMat.emissive = new pc.Color(1.0, 0.7, 0.4);
    shockMat.emissiveIntensity = 4.0;
    shockMat.useLighting = false;
    shockMat.blendType = pc.BLEND_ADDITIVE;
    shockMat.depthWrite = false;
    shockMat.opacity = 0.8;
    shockMat.update();
    if (shockwave.render) {
      shockwave.render.material = shockMat;
      shockwave.render.castShadows = false;
      shockwave.render.receiveShadows = false;
    }
    shockwave.setLocalPosition(0, -0.3, 0);
    root.addChild(shockwave);

    // 5. Flash core (rapidly expanding bright sphere that fades instantly)
    const flashCore = new pc.Entity("he-flash-core");
    flashCore.addComponent("render", { type: "sphere" });
    const flashMat = new pc.StandardMaterial();
    flashMat.diffuse = new pc.Color(0, 0, 0);
    flashMat.emissive = new pc.Color(1.0, 0.9, 0.6);
    flashMat.emissiveIntensity = 12.0;
    flashMat.useLighting = false;
    flashMat.blendType = pc.BLEND_ADDITIVE;
    flashMat.depthWrite = false;
    flashMat.opacity = 1.0;
    flashMat.update();
    if (flashCore.render) {
      flashCore.render.material = flashMat;
      flashCore.render.castShadows = false;
      flashCore.render.receiveShadows = false;
    }
    root.addChild(flashCore);

    this.app.root.addChild(root);

    // Screen shake — strong burst, decays.
    this.shakePitch += (Math.random() - 0.5) * 16;
    this.shakeYaw += (Math.random() - 0.5) * 16;

    const DURATION = 3.5;
    this.active.push({
      age: 0,
      duration: DURATION,
      tick: (_dt, age) => {
        const t = age / DURATION;

        // Shockwave ring — expands fast and is gone within 0.4s. It's an
        // additive material, where alpha does NOT scale the blend, so fading
        // `opacity` alone leaves it fully bright until the effect ends. Fade
        // the emissive to black and disable it so the ring truly disappears.
        const tSw = Math.min(1, age / 0.4);
        const swFade = Math.max(0, 1 - Math.pow(tSw, 1.2));
        const swScale = 0.5 + 14.0 * Math.pow(tSw, 0.4); // Ease out expand
        shockwave.setLocalScale(swScale, 0.05, swScale);
        shockMat.opacity = 0.8 * swFade;
        shockMat.emissiveIntensity = 4.0 * swFade;
        shockMat.update();
        if (tSw >= 1) shockwave.enabled = false;

        // Flash core — the bright white "ball". Same additive caveat as the
        // ring: fade the emissive (not just opacity) and disable it, otherwise
        // the ball hangs in the air after the dust has cleared.
        const tCore = Math.min(1, age / 0.15);
        const coreFade = Math.max(0, 1 - Math.pow(tCore, 1.5));
        const coreScale = 0.5 + 6.0 * Math.pow(tCore, 0.3);
        flashCore.setLocalScale(coreScale, coreScale, coreScale);
        flashMat.opacity = coreFade;
        flashMat.emissiveIntensity = 12.0 * coreFade;
        flashMat.update();
        if (tCore >= 1) flashCore.enabled = false;

        // Light fades
        const lc = light.light;
        if (lc) lc.intensity = Math.max(0, 25 * Math.pow(1 - t, 5)); // Fast decay
      },
      end: () => root.destroy(),
    });
  }

  // ── Breakable glass shatter ───────────────────────────────────────────

  /** Build the glass-shard texture once; shared by every shatter burst. */
  private getShardTexture(): pc.Texture {
    if (!this.shardTex) this.shardTex = createShardTexture(this.app);
    return this.shardTex;
  }

  /**
   * One-shot glass shatter burst at the bullet impact point — a quick
   * spray of bright light-blue/white shards. Server-authoritative; called
   * from the `glass_break` event wiring in main.ts. Pushed onto `active`
   * so it's reclaimed by clearAll() on round end like every other effect.
   */
  shatter(pos: pc.Vec3): void {
    const root = new pc.Entity("glass-shatter");
    root.setPosition(pos.x, pos.y, pos.z);

    const shardTex = this.getShardTexture();

    const shards = new pc.Entity("glass-shards");
    shards.addComponent("particlesystem", {
      numParticles: 30,
      lifetime: 0.6,
      rate: 0, // single burst, no continuous emission
      colorMap: shardTex,
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.25,
      velocityGraph: new pc.CurveSet([[0, 6, 1, 0], [0, 5, 1, -8], [0, 6, 1, 0]]),
      velocityGraph2: new pc.CurveSet([[0, -6, 1, 0], [0, 1, 1, -8], [0, -6, 1, 0]]),
      scaleGraph: new pc.Curve([0, 0.18, 1, 0.04]),
      alphaGraph: new pc.Curve([0, 1, 0.6, 1, 1, 0]),
      colorGraph: new pc.CurveSet([
        [0, 1, 1, 0.75],
        [0, 1, 1, 0.85],
        [0, 1, 1, 1.0],
      ]),
      blendType: pc.BLEND_NORMAL,
      loop: false,
      depthWrite: false,
      startAngle: 0,
      startAngle2: 360,
    });
    root.addChild(shards);
    if (shards.particlesystem) shards.particlesystem.play();

    this.app.root.addChild(root);

    const DURATION = 0.7;
    this.active.push({
      age: 0,
      duration: DURATION,
      tick: () => { /* particles self-animate via their graphs */ },
      end: () => root.destroy(),
    });
  }

  // ── Flashbang ─────────────────────────────────────────────────────────

  private spawnFlashbang(pos: pc.Vec3): void {
    // Cosmetic world pop only. The actual blind (white-out) is
    // server-authoritative — see `applyBlind`, driven by the `flash_blind`
    // message — so a flash behind a wall no longer blinds through it.
    const root = new pc.Entity("flash-light");
    root.setPosition(pos.x, pos.y + 0.5, pos.z);
    root.addComponent("light", {
      type: "omni",
      color: new pc.Color(1, 1, 1),
      intensity: 16,
      range: 26,
      castShadows: false,
    });
    this.app.root.addChild(root);

    const DURATION = 0.35;
    this.active.push({
      age: 0,
      duration: DURATION,
      tick: (_dt, age) => {
        const t = age / DURATION;
        const lc = root.light;
        if (lc) lc.intensity = Math.max(0, 12 * (1 - t * 1.5));
      },
      end: () => root.destroy(),
    });
  }

  /**
   * Render the server-authoritative flash white-out. The server already
   * decided exposure (line-of-sight through walls + view angle + distance)
   * and sent the peak opacity + hold + fade — we just drive the overlay.
   */
  applyBlind(peak: number, holdMs: number, fadeMs: number): void {
    const overlay = document.getElementById("flash-overlay");
    if (!overlay || !(peak > 0)) return;
    overlay.style.transition = "opacity 40ms linear";
    overlay.style.opacity = String(Math.max(0, Math.min(1, peak)));
    window.setTimeout(() => {
      overlay.style.transition = `opacity ${Math.max(0, fadeMs)}ms ease-out`;
      overlay.style.opacity = "0";
    }, Math.max(0, holdMs));
  }

  // ── Smoke cloud (persistent, server-driven zone) ──────────────────────

  /** Build the soft puff texture once; shared by every smoke cloud. */
  private getSmokeTexture(): pc.Texture {
    if (!this.smokeTex) this.smokeTex = createSmokeTexture(this.app);
    return this.smokeTex;
  }

  /**
   * Create a persistent volumetric smoke cloud for a server `zone_state`
   * entry. Position, opacity (fade in / out) and teardown are driven by the
   * caller (PeerGrenadeRenderer) from server broadcasts; GrenadeEffects owns
   * the per-frame billboarding + roil since it holds the camera + update
   * loop. Returns a handle to drive + destroy.
   */
  createSmokeZone(radius: number): SmokeCloud {
    const cloud = new SmokeCloud(this.app, this.camera, radius, this.getSmokeTexture());
    this.smokeZones.add(cloud);
    return cloud;
  }

  // ── Molotov / incendiary fire (persistent, server-driven zone) ──────

  /** Build the flame-tongue texture once; shared by every fire zone. */
  private getFireTexture(): pc.Texture {
    if (!this.fireTex) this.fireTex = createFireTexture(this.app);
    return this.fireTex;
  }

  /**
   * Create a persistent CS:GO-style fire pool for a server `zone_state`
   * entry (molotov / incendiary). Position, opacity (fade in / out) and
   * teardown are driven by the caller (PeerGrenadeRenderer); GrenadeEffects
   * owns the per-frame billboarding, flicker and rise animation since it
   * holds the camera + update loop. Returns a handle to drive + destroy.
   */
  createFireZone(radius: number): FireZone {
    const fire = new FireZone(this.app, this.camera, radius, this.getFireTexture(), this.getSmokeTexture());
    this.fireZones.add(fire);
    return fire;
  }
}

/**
 * A persistent volumetric smoke cloud (CS:GO style): a sphere packed with
 * soft, camera-facing billboard puffs. Built once when the server reports a
 * smoke `zone_state`, then driven by GrenadeEffects each frame (billboard +
 * gentle roil) and by the caller per server tick (world position + master
 * opacity via setAlpha). Replaces the old flat grey emissive spheres.
 */
export class SmokeCloud {
  readonly root: pc.Entity;
  /** False once destroyed — GrenadeEffects prunes dead clouds from its set. */
  alive = true;
  private readonly camera: pc.Entity;
  private readonly puffs: SmokePuff[] = [];
  private alpha = 0;
  private age = 0;
  /** Camera distances where the in-smoke screen fog starts / saturates. */
  private readonly fogOuter: number;
  private readonly fogInner: number;

  constructor(app: pc.AppBase, camera: pc.Entity, radius: number, tex: pc.Texture) {
    this.camera = camera;
    this.root = new pc.Entity("smoke-zone");
    app.root.addChild(this.root);

    // Visual radius: render the cloud bigger than the server vision-block
    // radius so it reads as a chunky CS:GO dome. Client-only — gameplay
    // (SMOKE_RADIUS) is unchanged; the dense core still covers the block.
    const r = radius * 1.4;
    // Screen fog ramps in from the visual shell and is fully grey inside
    // the dense core, so crossing the cloud blinds for its whole width.
    this.fogOuter = r;
    this.fogInner = radius * 0.85;
    // Puff coverage thins roughly with 1/r (count × puff-area / volume), so
    // keep the count proportional to the server radius or a bigger smoke
    // reads wispy. 20/m with the old 50-puff density at radius 2.5 as floor.
    const PUFF_COUNT = Math.max(50, Math.round(radius * 20));
    for (let i = 0; i < PUFF_COUNT; i++) {
      // Pick a point inside the zone sphere (only slightly squashed so the
      // cloud reads as a big, near-spherical CS:GO dome). Puffs fill most of
      // the radius so the dense core covers the whole vision-block volume.
      const dir = new pc.Vec3(
        Math.random() - 0.5,
        (Math.random() - 0.5) * 0.95,
        Math.random() - 0.5,
      );
      if (dir.length() < 1e-4) dir.set(0, 1, 0);
      dir.normalize();
      const dist = r * 0.82 * Math.cbrt(Math.random());
      const bx = dir.x * dist;
      const by = dir.y * dist;
      const bz = dir.z * dist;

      // entity (billboarded to camera) → spin (organic Z roll) → mesh (plane).
      const ent = new pc.Entity(`smoke-puff-${i}`);
      ent.setLocalPosition(bx, by, bz);
      this.root.addChild(ent);

      const spin = new pc.Entity(`smoke-puff-${i}-spin`);
      ent.addChild(spin);

      const mesh = new pc.Entity(`smoke-puff-${i}-mesh`);
      mesh.addComponent("render", { type: "plane" });
      mesh.setLocalEulerAngles(90, 0, 0);
      spin.addChild(mesh);

      const mat = new pc.StandardMaterial();
      mat.useLighting = false;
      mat.emissiveMap = tex;
      mat.opacityMap = tex;
      mat.opacity = 0;
      mat.blendType = pc.BLEND_NORMAL;
      mat.depthWrite = false;
      mat.update();
      if (mesh.render) {
        mesh.render.material = mat;
        mesh.render.castShadows = false;
        mesh.render.receiveShadows = false;
      }

      const scale = r * (1.15 + Math.random() * 0.65);
      spin.setLocalScale(scale, scale, scale);

      this.puffs.push({
        entity: ent,
        spin,
        mat,
        baseScale: scale,
        baseX: bx,
        baseY: by,
        baseZ: bz,
        rotZ: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 12,
        bobPhase: Math.random() * Math.PI * 2,
        bobAmp: 0.04 + Math.random() * 0.08,
        opacityMul: 0.92 + Math.random() * 0.08,
      });
    }
  }

  /** Master opacity (0..1) — the server-driven fade in / fade out. */
  setAlpha(a: number): void {
    this.alpha = a;
  }

  /**
   * Screen-fog factor (0..1) for a camera at `pos`. The billboard puffs
   * read solid from afar but thin out when the camera stands between them
   * (and the near plane slices the closest quads), so walking through the
   * cloud felt tiny and see-through. The DOM overlay driven by this ramps
   * up as the camera sinks past the visual shell and is fully grey inside
   * the dense vision-block core, scaled by the server-driven fade so
   * spawning / expiring clouds fog proportionally.
   */
  fogAt(pos: pc.Vec3): number {
    if (!this.alive || this.alpha <= 0) return 0;
    const c = this.root.getPosition();
    const dx = pos.x - c.x;
    const dy = pos.y - c.y;
    const dz = pos.z - c.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist >= this.fogOuter) return 0;
    const t = Math.min(1, (this.fogOuter - dist) / (this.fogOuter - this.fogInner));
    const eased = t * t * (3 - 2 * t); // smoothstep — soft shell entry
    return eased * this.alpha;
  }

  /** Per-frame: billboard to camera, gentle roil, quick bloom on spawn. */
  tick(dt: number): void {
    if (!this.alive) return;
    this.age += dt;
    const bloom = Math.min(1, this.age / 0.6); // rapid expand as the smoke pops
    const camRot = this.camera.getRotation();
    for (const p of this.puffs) {
      // Face the camera, then roll about the view axis for organic variation.
      p.entity.setRotation(camRot);
      p.rotZ += p.rotSpeed * dt;
      p.spin.setLocalEulerAngles(0, 0, p.rotZ);
      // Gentle vertical bob around the base position keeps the cloud alive.
      p.bobPhase += dt * 0.6;
      p.entity.setLocalPosition(p.baseX, p.baseY + Math.sin(p.bobPhase) * p.bobAmp, p.baseZ);
      // Fake volume shading — darker near the ground, lighter up top.
      const heightNorm = Math.max(0, Math.min(1, (p.baseY + 1.5) / 3));
      const tone = 0.4 + heightNorm * 0.28;
      p.mat.emissive.set(tone, tone, tone + 0.02);
      // Bloom the scale up on spawn, then hold.
      const s = p.baseScale * (0.35 + 0.65 * bloom);
      p.spin.setLocalScale(s, s, s);
      // Opacity from the server fade × slight per-puff variation.
      p.mat.opacity = this.alpha * p.opacityMul;
      p.mat.update();
    }
  }

  destroy(): void {
    if (!this.alive) return;
    this.alive = false;
    this.root.destroy();
  }
}

interface SmokePuff {
  entity: pc.Entity;
  spin: pc.Entity;
  mat: pc.StandardMaterial;
  baseScale: number;
  baseX: number;
  baseY: number;
  baseZ: number;
  rotZ: number;
  rotSpeed: number;
  bobPhase: number;
  bobAmp: number;
  opacityMul: number;
}

/**
 * A persistent CS:GO-style fire pool (molotov / incendiary). Built once from a
 * server fire `zone_state`, then driven each frame by GrenadeEffects (it holds
 * the camera + update loop) and per server tick by the caller (world position +
 * master intensity via setAlpha). Replaces the old additive sphere blobs.
 *
 * Layered for a real-fire look using optimized PlayCanvas particle systems.
 */
export class FireZone {
  readonly root: pc.Entity;
  /** False once destroyed — GrenadeEffects prunes dead zones from its set. */
  alive = true;
  private readonly camera: pc.Entity;
  private readonly lights: FireLight[] = [];
  private alpha = 0;
  private age = 0;
  
  private baseFire: pc.Entity;
  private riseFire: pc.Entity;

  constructor(app: pc.AppBase, camera: pc.Entity, radius: number, fireTex: pc.Texture, smokeTex: pc.Texture) {
    this.camera = camera;
    this.root = new pc.Entity("fire-zone");
    app.root.addChild(this.root);

    // Shared color curve: White -> Yellow -> Orange -> Dark Red
    const colorCurve = new pc.CurveSet([
      [0, 1, 0.2, 1, 0.6, 1, 1, 0.6], // R
      [0, 1, 0.2, 0.8, 0.6, 0.3, 1, 0.1], // G
      [0, 1, 0.2, 0.2, 0.6, 0.0, 1, 0.0]  // B
    ]);

    // 1. Base fire (stays near ground, spreads out horizontally)
    const baseScaleCurve = new pc.Curve([0, 0.5, 0.3, 1.8, 0.8, 1.5, 1, 0.5]);
    const baseAlphaCurve = new pc.Curve([0, 0, 0.2, 1, 0.8, 1, 1, 0]);
    const baseVelocityCurve = new pc.CurveSet([
      [0, 0],
      [0, 0.1, 1, 0.25], // slow upward — keep the base low
      [0, 0]
    ]);

    this.baseFire = new pc.Entity("base-fire-particles");
    this.baseFire.addComponent("particlesystem", {
      numParticles: 80,
      lifetime: 1.2,
      rate: 0.04,
      colorMap: fireTex,
      emitterShape: pc.EMITTERSHAPE_BOX,
      emitterExtents: new pc.Vec3(radius, 0.05, radius),
      velocityGraph: baseVelocityCurve,
      scaleGraph: baseScaleCurve,
      alphaGraph: baseAlphaCurve,
      colorGraph: colorCurve,
      blendType: pc.BLEND_ADDITIVE,
      loop: true,
      depthWrite: false,
      startAngle: 0,
      startAngle2: 360,
    });
    this.baseFire.setLocalPosition(0, 0.05, 0);
    this.root.addChild(this.baseFire);

    // 2. Rising flames (moves up faster, smaller particles)
    const riseScaleCurve = new pc.Curve([0, 0.3, 0.5, 0.9, 1, 0.2]);
    const riseAlphaCurve = new pc.Curve([0, 0, 0.2, 0.9, 0.7, 0.9, 1, 0]);
    const riseVelocityCurve = new pc.CurveSet([
      [0, 0],
      [0, 0.7, 1, 1.5], // gentle upward — keep flames low so they don't block vision
      [0, 0]
    ]);

    this.riseFire = new pc.Entity("rise-fire-particles");
    this.riseFire.addComponent("particlesystem", {
      numParticles: 120,
      lifetime: 1.0,
      rate: 0.02,
      colorMap: fireTex,
      emitterShape: pc.EMITTERSHAPE_BOX,
      emitterExtents: new pc.Vec3(radius * 0.85, 0.1, radius * 0.85),
      velocityGraph: riseVelocityCurve,
      scaleGraph: riseScaleCurve,
      alphaGraph: riseAlphaCurve,
      colorGraph: colorCurve,
      blendType: pc.BLEND_ADDITIVE,
      loop: true,
      depthWrite: false,
      startAngle: 0,
      startAngle2: 360,
    });
    this.riseFire.setLocalPosition(0, 0.1, 0);
    this.root.addChild(this.riseFire);

    // --- Lights: capped + shared + flickering (centre + up to 2 offset). ---
    const LIGHT_COUNT = radius > 1.8 ? 3 : 1;
    for (let i = 0; i < LIGHT_COUNT; i++) {
      const isCentre = i === 0;
      const ang = (i / LIGHT_COUNT) * Math.PI * 2;
      const lr = isCentre ? 0 : radius * 0.55;
      const le = new pc.Entity(`fire-light-${i}`);
      le.addComponent("light", {
        type: "omni",
        color: new pc.Color(1.0, 0.5, 0.18),
        intensity: 0,
        range: radius * (isCentre ? 2.8 : 1.9),
        castShadows: false,
      });
      le.setLocalPosition(Math.cos(ang) * lr, 0.5, Math.sin(ang) * lr);
      this.root.addChild(le);
      this.lights.push({
        entity: le,
        base: isCentre ? 3.0 : 1.4,
        seed: Math.random() * 100,
        hz: 7 + Math.random() * 6,
      });
    }
  }

  /** Master intensity (0..1) — the server-driven fade in / fade out. */
  setAlpha(a: number): void {
    this.alpha = a;
    
    // Stop emitting particles smoothly near the end
    if (this.alpha < 0.15 && this.age > 1.0) {
      if (this.baseFire.particlesystem) this.baseFire.particlesystem.loop = false;
      if (this.riseFire.particlesystem) this.riseFire.particlesystem.loop = false;
    }
  }

  /** Per-frame: animate every layer, billboard to camera; ignition flare. */
  tick(dt: number): void {
    if (!this.alive) return;
    this.age += dt;
    const flare = 1 + 0.7 * Math.max(0, 1 - this.age / 0.4); // ignition pop

    // --- Lights: flicker intensity, scaled by the server fade + flare. ---
    for (const l of this.lights) {
      const lc = l.entity.light;
      if (!lc) continue;
      const flick = 0.7 + 0.3 * Math.sin(this.age * l.hz + l.seed);
      lc.intensity = l.base * flick * this.alpha * flare;
    }
  }

  destroy(): void {
    if (!this.alive) return;
    this.alive = false;
    this.root.destroy();
  }
}

interface FireLight {
  entity: pc.Entity;
  base: number;
  seed: number;
  hz: number;
}
